import { createContext, useContext, useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { AxiosError } from 'axios';
import { CoA, Entity, Profile } from '@/interfaces/auth';
import { isAdmin as checkIsAdmin } from '@/lib/is-admin';
import { DonViChiNhanh } from '@/types/schemas';
import { toast } from '@/hooks/use-toast';
import { logout } from '@/lib/auth';
import api from '@/lib/api';

type PermissionActionKey = 'view' | 'create' | 'update' | 'delete' | 'approve';
type PermissionMap = Record<string, Partial<Record<PermissionActionKey, boolean>>>;

interface AuthContextType {
  profile: Profile | null;
  entity: Entity | null;
  coa: CoA | null;
  entityUnit: DonViChiNhanh | null;
  permissions: PermissionMap;
  authInitialized: boolean;
  loading: boolean;
  error: Error | null;
  isAdmin: boolean;
  refetchProfile: () => Promise<void>;
  setLoading: (loading: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [entity, setEntity] = useState<Entity | null>(null);
  const [coa, setCoa] = useState<CoA | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [entityUnit, setEntityUnit] = useState<DonViChiNhanh | null>(null);
  const [permissions, setPermissions] = useState<PermissionMap>({});
  const [authInitialized, setAuthInitialized] = useState(false);

  const fetchProfile = async () => {
    try {
      const response = await api.get<Profile>('/profile/');

      if ((response.data as any)?.entity?.name?.toUpperCase().includes('TEST')) {
        toast({
          description: 'Vui lòng đăng nhập tài khoản khác',
          variant: 'destructive'
        });
        return response.data;
      }
      setProfile(response.data);
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
      throw err;
    }
  };

  const fetchEntity = async (entitySlug: string) => {
    try {
      const response = await api.get<Entity>(`/entities/${entitySlug}/`);
      setEntity(response.data);
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch entity'));
      throw err;
    }
  };

  const fetchCoa = async (entitySlug: string, coaSlug: string) => {
    try {
      const response = await api.get<CoA>(`/entities/${entitySlug}/coa/${coaSlug}/`);
      setCoa(response.data);
      return response.data;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch CoA'));
      throw err;
    }
  };

  const fetchDefaultEntityUnit = async (entitySlug: string) => {
    try {
      const response = await api.get(`/entities/${entitySlug}/erp/entity-units/`);
      const units = response.data.results || response.data;
      const defaultUnit = units.find((unit: DonViChiNhanh) => unit.is_default === true);
      setEntityUnit(defaultUnit || null);
      return defaultUnit;
    } catch (err) {
      console.error('Error fetching default entity unit:', err);
      setEntityUnit(null);
    }
  };

  const fetchMyPermissions = async (entitySlug: string) => {
    try {
      const response = await api.get<{ permissions?: PermissionMap }>(
        `/entities/${entitySlug}/erp/he-thong/tai-khoan/phan-quyen/my-permission/`
      );
      setPermissions(response.data.permissions ?? {});
      return response.data.permissions ?? {};
    } catch (err) {
      console.error('Error fetching my permissions:', err);
      setPermissions({});
      return {};
    }
  };

  const loadAuthData = async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      setAuthInitialized(true);
      if (!window.location.href.includes('/xac-thuc')) {
        window.location.href = '/xac-thuc/';
      }

      return;
    }

    setLoading(true);
    setError(null);
    try {
      const profileData = await fetchProfile();
      const entitySlug = profileData?.erp_active_entity_slug || profileData?.entity_slug;
      if (entitySlug) {
        const entityData = await fetchEntity(entitySlug);
        if (entityData?.default_coa_slug) {
          await fetchCoa(entityData.slug, entityData.default_coa_slug);
        }
        await fetchMyPermissions(entityData.slug);
        // Fetch default entity unit after entity is loaded
        await fetchDefaultEntityUnit(entityData.slug);
      }
    } catch (err: unknown) {
      console.error('Error loading auth data:', err);
      // Only redirect on 401 - other errors should be handled by the api interceptor
      if (err instanceof AxiosError && err.response?.status === 401) {
        window.location.href = '/xac-thuc';
      }
    } finally {
      setLoading(false);
      setAuthInitialized(true);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (!token && !window.location.href.includes('/xac-thuc')) {
      setLoading(false);
      setAuthInitialized(true);
      window.location.href = '/xac-thuc';
      return;
    }

    loadAuthData();
  }, []);

  const refetchProfile = async () => {
    await loadAuthData();
  };

  const isAdmin = checkIsAdmin(profile);

  const value = {
    profile,
    entity,
    coa,
    entityUnit,
    permissions,
    authInitialized,
    loading,
    error,
    isAdmin,
    refetchProfile,
    setLoading
  };

  if (authInitialized && profile && !isAdmin && Object.keys(permissions).length === 0) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 dark:bg-slate-900'>
        <div className='w-full max-w-md rounded-2xl border border-slate-100 bg-white p-8 text-center shadow-xl dark:border-slate-700 dark:bg-slate-800'>
          <div className='mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500 dark:bg-red-950/50 dark:text-red-400'>
            <ShieldAlert className='h-10 w-10' />
          </div>
          <h2 className='animate-fade-in mb-2 text-xl font-semibold text-slate-800 dark:text-slate-100'>
            Tài khoản này không có quyền truy cập
          </h2>
          <p className='mb-8 text-sm text-slate-600 dark:text-slate-400'>
            Tài khoản của bạn chưa được phân quyền sử dụng hệ thống này. Vui lòng liên hệ quản trị viên.
          </p>
          <button
            onClick={() => logout()}
            className='w-full rounded-xl bg-red-600 py-3 font-medium text-white shadow-lg shadow-red-500/20 transition-all hover:bg-red-700 hover:shadow-red-500/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900'
          >
            Đăng xuất
          </button>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
